import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { API } from "../App";
import { toast, Toaster } from "sonner";
import {
  AlertTriangle,
  Send,
  Coins,
  Package,
  HelpCircle,
  CheckCircle,
  Flame,
  User,
  Megaphone,
  Info
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";

const problemTypes = [
  {
    id: "money_lost",
    label: "Geld weg, kein Produkt",
    icon: Coins,
    color: "text-amber-600 bg-amber-100 border-amber-300",
    description: "Geld eingeworfen aber kein Produkt erhalten"
  },
  {
    id: "empty_slot",
    label: "Fach leer",
    icon: Package,
    color: "text-rose-600 bg-rose-100 border-rose-300",
    description: "Ein Fach ist leer, obwohl es gefüllt sein sollte"
  },
  {
    id: "other",
    label: "Sonstiges Problem",
    icon: HelpCircle,
    color: "text-blue-600 bg-blue-100 border-blue-300",
    description: "Anderes Problem mit dem Automaten"
  }
];

const ProblemPage = () => {
  const [name, setName] = useState("");
  const [problemType, setProblemType] = useState("");
  const [slotId, setSlotId] = useState("");
  const [amount, setAmount] = useState("");
  const [description, setDescription] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [announcements, setAnnouncements] = useState([]);

  useEffect(() => {
    // Fetch announcements for problem page
    axios.get(`${API}/public/wish-announcements?page=problem`)
      .then(res => setAnnouncements(res.data || []))
      .catch(() => {});
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!name.trim()) {
      toast.error("Bitte Name eingeben");
      return;
    }
    if (!problemType) {
      toast.error("Bitte Problemtyp auswählen");
      return;
    }

    setSubmitting(true);
    try {
      await axios.post(`${API}/problem-reports`, {
        reporter_name: name.trim(),
        problem_type: problemType,
        slot_id: slotId.trim(),
        amount: parseFloat(amount) || 0,
        description: description.trim()
      });
      setSubmitted(true);
      toast.success("Meldung erfolgreich eingereicht!");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Fehler beim Senden");
    } finally {
      setSubmitting(false);
    }
  };

  const resetForm = () => {
    setName("");
    setProblemType("");
    setSlotId("");
    setAmount("");
    setDescription("");
    setSubmitted(false);
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center p-4">
        <Toaster position="top-center" richColors />
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="w-full max-w-md"
        >
          <Card className="border-emerald-500/30 bg-slate-800/50 backdrop-blur">
            <CardContent className="pt-8 pb-8 text-center">
              <div className="w-20 h-20 bg-emerald-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
                <CheckCircle className="w-10 h-10 text-emerald-400" />
              </div>
              <h2 className="text-2xl font-heading font-bold text-white mb-2">
                Vielen Dank!
              </h2>
              <p className="text-slate-300 mb-6">
                Ihre Meldung wurde erfolgreich eingereicht. Wir kümmern uns darum!
              </p>
              <Button
                onClick={resetForm}
                variant="outline"
                className="border-slate-600 text-slate-300 hover:bg-slate-700"
              >
                Neue Meldung
              </Button>
            </CardContent>
          </Card>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 py-8 px-4 overflow-auto" style={{ position: 'fixed', inset: 0, overflowY: 'auto' }}>
      <Toaster position="top-center" richColors />
      
      <div className="max-w-lg mx-auto">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-8"
        >
          <div className="flex items-center justify-center gap-3 mb-4">
            <div className="w-12 h-12 bg-rose-600 rounded-xl flex items-center justify-center">
              <Flame className="w-7 h-7 text-white" />
            </div>
            <h1 className="text-2xl font-heading font-bold text-white">
              Lünefire
            </h1>
          </div>
          <h2 className="text-xl text-slate-300 flex items-center justify-center gap-2">
            <AlertTriangle className="w-5 h-5 text-amber-400" />
            Problem melden
          </h2>
        </motion.div>

        {/* Announcements */}
        {announcements.length > 0 && (
          <div className="space-y-3 mb-6">
            {announcements.map((announcement) => (
              <motion.div
                key={announcement.id}
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
              >
                <Card className={`
                  border text-white
                  ${announcement.type === 'success' ? 'bg-emerald-900/50 border-emerald-600' :
                    announcement.type === 'warning' ? 'bg-amber-900/50 border-amber-600' :
                    'bg-blue-900/50 border-blue-600'}
                `}>
                  <CardContent className="pt-4">
                    <div className="flex items-start gap-3">
                      <div className={`
                        p-2 rounded-lg
                        ${announcement.type === 'success' ? 'bg-emerald-500/20' :
                          announcement.type === 'warning' ? 'bg-amber-500/20' :
                          'bg-blue-500/20'}
                      `}>
                        {announcement.type === 'success' ? (
                          <CheckCircle className="w-5 h-5 text-emerald-400" />
                        ) : announcement.type === 'warning' ? (
                          <AlertTriangle className="w-5 h-5 text-amber-400" />
                        ) : (
                          <Megaphone className="w-5 h-5 text-blue-400" />
                        )}
                      </div>
                      <div className="flex-1">
                        {announcement.title && (
                          <h3 className="font-semibold text-white mb-1">{announcement.title}</h3>
                        )}
                        <p className="text-sm text-slate-300 whitespace-pre-wrap">
                          {announcement.message}
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        )}

        {/* Form */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
        >
          <Card className="border-slate-700 bg-slate-800/50 backdrop-blur">
            <CardHeader>
              <CardTitle className="text-white text-center">
                Was ist passiert?
              </CardTitle>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Name Field - Required */}
                <div className="space-y-2">
                  <Label className="text-slate-300 flex items-center gap-2">
                    <User className="w-4 h-4" />
                    Ihr Name *
                  </Label>
                  <Input
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="Max Mustermann"
                    className="bg-slate-700/50 border-slate-600 text-white placeholder:text-slate-400"
                    required
                    data-testid="problem-name-input"
                  />
                </div>

                {/* Problem Type Selection */}
                <div className="space-y-3">
                  <Label className="text-slate-300">Art des Problems *</Label>
                  <div className="grid gap-3">
                    {problemTypes.map((type) => {
                      const Icon = type.icon;
                      const isSelected = problemType === type.id;
                      return (
                        <button
                          key={type.id}
                          type="button"
                          onClick={() => setProblemType(type.id)}
                          className={`p-4 rounded-lg border-2 text-left transition-all ${
                            isSelected
                              ? `${type.color} border-current`
                              : "bg-slate-700/30 border-slate-600 hover:border-slate-500"
                          }`}
                          data-testid={`problem-type-${type.id}`}
                        >
                          <div className="flex items-center gap-3">
                            <Icon className={`w-5 h-5 ${isSelected ? "" : "text-slate-400"}`} />
                            <div>
                              <div className={`font-medium ${isSelected ? "" : "text-white"}`}>
                                {type.label}
                              </div>
                              <div className={`text-sm ${isSelected ? "opacity-80" : "text-slate-400"}`}>
                                {type.description}
                              </div>
                            </div>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* Conditional Fields */}
                {problemType === "money_lost" && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: "auto" }}
                    className="space-y-4"
                  >
                    <div className="space-y-2">
                      <Label className="text-slate-300">Betrag (€)</Label>
                      <Input
                        type="number"
                        step="0.10"
                        min="0"
                        value={amount}
                        onChange={(e) => setAmount(e.target.value)}
                        placeholder="z.B. 1.50"
                        className="bg-slate-700/50 border-slate-600 text-white placeholder:text-slate-400"
                        data-testid="problem-amount-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-slate-300">Fach-Nr. (falls bekannt)</Label>
                      <Input
                        value={slotId}
                        onChange={(e) => setSlotId(e.target.value)}
                        placeholder="z.B. A1, B3"
                        className="bg-slate-700/50 border-slate-600 text-white placeholder:text-slate-400"
                        data-testid="problem-slot-input"
                      />
                    </div>
                  </motion.div>
                )}

                {problemType === "empty_slot" && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: "auto" }}
                    className="space-y-2"
                  >
                    <Label className="text-slate-300">Welches Fach ist leer?</Label>
                    <Input
                      value={slotId}
                      onChange={(e) => setSlotId(e.target.value)}
                      placeholder="z.B. A1, B3"
                      className="bg-slate-700/50 border-slate-600 text-white placeholder:text-slate-400"
                      data-testid="problem-slot-input"
                    />
                  </motion.div>
                )}

                {/* Description */}
                <div className="space-y-2">
                  <Label className="text-slate-300">Weitere Details (optional)</Label>
                  <Textarea
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    placeholder="Beschreiben Sie das Problem genauer..."
                    rows={3}
                    className="bg-slate-700/50 border-slate-600 text-white placeholder:text-slate-400 resize-none"
                    data-testid="problem-description-input"
                  />
                </div>

                {/* Submit Button */}
                <Button
                  type="submit"
                  disabled={submitting || !name.trim() || !problemType}
                  className="w-full bg-rose-600 hover:bg-rose-700 text-white py-6 text-lg"
                  data-testid="problem-submit-btn"
                >
                  {submitting ? (
                    "Wird gesendet..."
                  ) : (
                    <>
                      <Send className="w-5 h-5 mr-2" />
                      Meldung absenden
                    </>
                  )}
                </Button>
              </form>
            </CardContent>
          </Card>
        </motion.div>

        {/* Footer */}
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.3 }}
          className="text-center text-slate-500 text-sm mt-6"
        >
          Feuerwehr Lüneburg • Snackautomat
        </motion.p>
      </div>
    </div>
  );
};

export default ProblemPage;
